import {
  Box,
  Button,
  ChipPropsVariantOverrides,
  CircularProgress,
  circularProgressClasses,
  Stack,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { menuUtil } from "../../../common";
import { formatTimeUntilMeeting, formatCircularCountdown, getMeetingFillPercent } from "app/containers/TeamsTab/helpers";
import {
  LargeCustomIcon,
  SmallCustomIcon,
  TimelineMarker,
} from "../../layer00";
import { CriteriaIconButton } from "../../layer01";
import { CalendarAppointmentMenu } from "../../layer18";
import { OneOnOnePrepModal } from "../../layer16";
import { useUpdateMeetingStatusMutation } from "app/containers/TeamsTab/calendarApi";

export type CalendarAppointmentCardProps = {
  id: string;
  aptTime: DateTime;
  aptName: string;
  aptState: keyof ChipPropsVariantOverrides;
  meetingId: number;
  webLink?: string | null;
  userConfirmed?: boolean;
  userAccountId?: number | null;
  report?: {
    id: string;
  };
};

export const CalendarAppointmentCard = (
  props: CalendarAppointmentCardProps
) => {
  const menu = menuUtil.useBaseMenuState();
  const aptTime = props.aptTime.toLocal();
  const colors = __determineColorsForApt(aptTime);
  const countdown = formatCircularCountdown(aptTime);
  const fillPercent = getMeetingFillPercent(aptTime);
  const [isVisible, setIsVisible] = useState(false);
  const [updateMeetingStatus] = useUpdateMeetingStatusMutation();
  return (
    <Box position="relative">
      {isVisible && (
        <OneOnOnePrepModal
          isVisible={isVisible}
          handleClose={() => setIsVisible(() => false)}
          id={props.id}
          userAccountId={props.userAccountId}
        />
      )}
      <TimelineMarker overhang="0.75rem" />
      <Box
        className="bg-team-app-gradient-stripe-light border border-teamApp-stripe-mid"
        sx={{
          minHeight: "5.5rem",
          borderRadius: "1rem",
          padding: "1rem",
        }}
        display="flex"
        flexDirection={{
          xs: "column",
          sm: "row",
        }}
        gap="1rem"
        alignSelf="stretch"
        alignItems={{
          xs: "stretch",
          sm: "center",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          columnGap="1rem"
          minWidth={0}
          sx={{
            flex: {
              sm: "1 1 0",
            },
          }}
        >
          <Box position="relative" display="inline-flex">
            <CircularProgress
              size="3.5rem"
              variant="determinate"
              enableTrackSlot
              value={100 - fillPercent}
              sx={(theme) => ({
                color: `${colors.background}`,
                animationDuration: "550ms",

                [`& .${circularProgressClasses.circle}`]: {
                  strokeLinecap: "butt",
                },
                [`& .${circularProgressClasses.track}`]: {
                  opacity: 1,
                  fill: `${colors.background}`,
                  stroke: `${colors.border}`,

                  ...theme.applyStyles("dark", {
                    stroke: `${colors.border}`,
                  }),
                },
                ...theme.applyStyles("dark", {
                  color: `${colors.border}`,
                }),
              })}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="modalTitle"
                sx={{
                  lineHeight: ".75rem",
                }}
                color={colors.color}
              >
                {countdown.value}
              </Typography>
              <Typography variant="caption" color={colors.color}>
                {countdown.label}
              </Typography>
            </Box>
          </Box>

          <LargeCustomIcon
            icon="SEPERATOR"
            stroke="var(--team-app-divider-subtle)"
            className="w-auto h-[2.5rem]"
          />
          <Stack
            flex="1 1 0"
            direction="column"
            rowGap=".25rem"
            minHeight="1.25rem"
          >
            <Typography
              variant="cardBodyHeader"
              className="text-sapphire-150"
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
              }}
            >
              {props.aptName}
            </Typography>
            <Typography variant="cardBodyRegular">
              {aptTime.toLocaleString(DateTime.DATE_FULL)} -{" "}
              {formatTimeUntilMeeting(aptTime)}
            </Typography>
          </Stack>
        </Box>
        <Stack
          direction="row"
          alignSelf="stretch"
          height="2.5rem"
          columnGap=".5rem"
        >
          <Button
            sx={{
              padding: ".5rem",
              flex: {
                xs: 1,
                sm: "none",
              },
            }}
            variant="outlined"
            color="primary"
            onClick={() => setIsVisible(() => true)}
          >
            Prepare
          </Button>
          <CriteriaIconButton
            onClick={menu.handleClick}
            icon={<SmallCustomIcon icon="MOREVERTICAL" />}
            aria-label="Meeting options"
          />
        </Stack>
        {menu.open && (
          <CalendarAppointmentMenu
            {...menu}
            id=""
            onConfirm={
              props.userConfirmed
                ? undefined
                : () =>
                    updateMeetingStatus({
                      meetingId: props.meetingId,
                      action: "confirm",
                    })
            }
            onMarkAsNot1on1={() =>
              updateMeetingStatus({
                meetingId: props.meetingId,
                action: "reject",
              })
            }
            onViewInCalendar={
              props.webLink
                ? () => window.open(props.webLink ?? "", "_blank")
                : undefined
            }
          />
        )}
      </Box>
    </Box>
  );
};

const __determineColorsForApt = (aptTime: DateTime<boolean>) => {
  const minFromNow = aptTime.diffNow("minutes").minutes;

  let duration: number;

  if (minFromNow <= 30) {
    duration = 30;
  } else if (minFromNow <= 90) {
    duration = 90;
  } else {
    duration = 1000;
  }

  return {
    background: timeToApt2BackgroundColor[duration],
    border: timeToAptBorderColor[duration],
    color: timeToApt2Color[duration],
  };
};

const timeToApt2BackgroundColor: Record<number, string> = {
  30: "var(--team-app-warning-surface-warm)",
  90: "var(--sapphire-10)",
  1000: "var(--success-green-10)",
};

const timeToApt2Color: Record<number, string> = {
  30: "var(--team-app-score-moderate)",
  90: "var(--sapphire-130)",
  1000: "var(--success-green-120)",
};

const timeToAptBorderColor: Record<number, string> = {
  30: "var(--team-app-score-low-moderate)",
  90: "var(--sapphire-100)",
  1000: "var(--success-green-100)",
};
